import React from "react";
import { Link } from "react-router-dom";
import {
    Card,
    CardContent,
    Box,
    CardHeader,
    Typography,
    Button,
    Stack,
} from "@mui/material";

import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import RMSAvatar from "../RMSAvatar";
import AppraisalDetails from "./AppraisalDetails";
import AppraisalProgressDetails from "./AppraisalProgressDetails";
import { appraisalStatusMessage } from "./AppraisalFunctions";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import RenderDate from "../../../Common/Date/RenderDate";

function AppraisalButton({ id, status }) {
    let label = "View";
    let color = "neutral";
    let textColor = "rgba(0,0,0,0.7)";

    switch (status) {
        case "open":
            label = "Continue";
            color = "primary";
            textColor = "rgb(255.255.255)";
            break;

        default:
            break;
    }

    return (
        <Button
            variant={"contained"}
            color={color}
            disableElevation={true}
            aria-label="Appraisal"
            component={Link}
            to={`/rms/appraisals/${Number(id) || ""}`}
            endIcon={<KeyboardArrowRightOutlinedIcon />}
        >
            <Typography variant="span" sx={{ color: textColor }}>
                {label}
            </Typography>
        </Button>
    );
}

function AppraisalCard({ appraisal }) {
    const title = !!appraisal?.title?.length ? appraisal.title : "Appraisal";
    const status = appraisal?.status ?? "";

    let isCompleted = false;

    const alert = appraisalStatusMessage(
        appraisal?.status,
        appraisal?.inputs?.status ?? "not_started",
        appraisal?.meeting?.status ?? "",
        appraisal?.outputs?.status ?? "",
        appraisal?.survey?.status ?? "",
        false
    );

    const dueDate = !!appraisal?.dateDue ? appraisal.dateDue : null;

    switch (status) {
        case "completed":
            isCompleted = true;
            break;

        default:
            break;
    }

    return (
        <Card className="card">
            <CardHeader
                disableTypography={true}
                avatar={<RMSAvatar type="appraisal" badge={status} />}
                title={
                    <Typography component="h2" className="card-header-title">
                        {title}
                    </Typography>
                }
                subheader={
                    <Typography
                        component="span"
                        className="card-header-subtitle"
                    >
                        {!!appraisal?.cycle?.start && (
                            <RenderDate date={appraisal.cycle.start} />
                        )}
                        &nbsp;-&nbsp;
                        {!!appraisal?.cycle?.end && (
                            <RenderDate date={appraisal.cycle.end} />
                        )}
                    </Typography>
                }
                action={
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "8px" }}
                    >
                        <AppraisalButton id={appraisal.id} status={status} />
                    </Stack>
                }
            />
            <CardContent>
                <Box className="card-content">
                    <AppraisalDetails
                        template={appraisal?.origin?.template ?? "Default"}
                        appraisee={appraisal?.appraisee}
                        appraiser={appraisal?.appraiser}
                        areaOfWork={appraisal?.areaOfWork}
                        meeting={appraisal?.meeting}
                        isCompleted={isCompleted}
                        dueDate={dueDate}
                    />
                </Box>
            </CardContent>

            <CardContent>
                <Box className="card-content">
                    <AppraisalProgressDetails
                        inputs={appraisal.inputs}
                        outputs={appraisal.outputs}
                        meeting={appraisal.meeting}
                        survey={appraisal?.survey}
                        status={status}
                        display={
                            status === "completed" || status === "closed"
                                ? "summary"
                                : ""
                        }
                        appraisalId={appraisal.id}
                    />
                </Box>
            </CardContent>

            {alert?.message && (
                <CardContent>
                    <Box className="card-content">
                        <AlertMessage
                            severity={alert.severity}
                            message={alert.message}
                        />
                    </Box>
                </CardContent>
            )}
        </Card>
    );
}

export default React.memo(AppraisalCard);
